import { useEffect, useMemo, useState, useCallback } from 'react';
import { IconRegistry } from '@tylertech/forge';
import { ForgeButton, ForgeIcon } from '@tylertech/forge-react';
import { tylIconArrowBack } from '@tylertech/tyler-icons';
import { CONFIG } from '../../config/Config';
import { getCookie } from '../../utils/cookies';
import useProfile from '../../hooks/useProfile';
import IdleDialog from '../IdleDialog';
import './Layout.css';
import MobileNav from './MobileNav';
import PrimaryNav from './PrimaryNav';
import SideNav from './SideNav';
import scGovLogo from '../../assets/logo-scgov.svg';
import scGovLogoWhite from '../../assets/logo-scgov-white.svg';
import profileIcon from '../../assets/profile-icon.png';


IconRegistry.define([tylIconArrowBack]);

export default function Layout({ currentRoute, navRoutes, onNavigate, children }) {
  // `origin_app` reaches us two ways, with different meanings:
  //   query param — always set by the /sso CloudFront function, so it drives the label
  //   cookie      — set by /session only when a validated return_to was stored, so its
  //                 presence tells us there is a specific page to return to
  const originAppCookie = getCookie('origin_app');
  const originApp = new URLSearchParams(window.location.search).get('origin_app') || originAppCookie;
  const hasReturnTarget = Boolean(originAppCookie);
  const { profile } = useProfile();
  const displayName = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ') || 'User';
  const [actionState, setActionState] = useState({ label: null, disabled: false });
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);

  const toggleHeaderMenu = useCallback(() => {
    setHeaderMenuOpen(prev => !prev);
  }, []);

  useEffect(() => {
    if (currentRoute?.key !== 'edit') {
      setActionState({ label: null, disabled: false });
      return;
    }

    const handleStatus = event => {
      const { label = null, disabled = false } = event.detail || {};
      setActionState({ label, disabled });
    };

    window.addEventListener('edit-profile:status', handleStatus);
    return () => window.removeEventListener('edit-profile:status', handleStatus);
  }, [currentRoute?.key]);

  const action = currentRoute?.headerAction;
  const secondaryAction = currentRoute?.headerSecondaryAction;
  const activeNavKey = currentRoute?.navKey || currentRoute?.key;

  // Route on whether a return target exists; /redirect resolves the HttpOnly return_to cookie.
  // With no return target, fall back to the SC.gov dashboard rather than the homepage.
  const returnHref = hasReturnTarget
    ? `${CONFIG.API_BASE_PATH}/redirect`
    : `${CONFIG.SC_GOV_URL}/dashboard`;

  // Label follows the destination, so a return_to that failed the backend allow-list check
  // (cookie silently dropped) still reads correctly for the SC.gov fallback.
  const returnLabel = !hasReturnTarget || originApp === 'SC'
    ? 'Back to SC.gov'
    : 'Back to Application';

  const handleReturnClick = (e) => {
    e.preventDefault();
    window.location.href = returnHref;
  };

  const handleSecondaryActionClick = () => {
    if (secondaryAction?.target) {
      onNavigate(secondaryAction.target);
    }
  };

  const handleActionClick = () => {
    if (actionState.disabled) return;
    if (action?.label?.toLowerCase() === 'save' && currentRoute?.key === 'edit') {
      const saveEvent = new CustomEvent('edit-profile:save', {
        detail: {
          onSuccess: () => {
            if (action.target) {
              onNavigate(action.target);
            }
          },
        },
      });
      window.dispatchEvent(saveEvent);
      return;
    }

    if (action?.target) {
      onNavigate(action.target);
    }
  };

  // Build mobile navigation routes structure
  const mobileNavRoutes = useMemo(() => [
    {
      key: 'dashboard',
      label: 'My Dashboard',
      path: 'dashboard',
      href: CONFIG.PORTAL_DASHBOARD_URL
    },
    {
      key: 'my-account',
      label: 'My Account',
      path: 'my-account',
      children: navRoutes.map(route => ({
        key: route.key,
        label: route.navLabel,
        path: 'my-account/' + route.path,
        href: route.hash
      }))
    }
  ], [navRoutes]);

  const activePath = useMemo(() => 'my-account/' + (currentRoute?.path || ''), [currentRoute?.path]);

  return (
    <div className="layout container">
      <div className="site-banner grid-item">
        <div className="site-banner__inner">
          <span className="site-banner__text">The Official Website of the State of <strong>South Carolina</strong></span>
          <div className="site-banner__links">
            <a href={`${CONFIG.SC_GOV_URL}/help-center`} className="site-banner__link">Help Center</a>
            <span className="site-banner__user">
              <span>{displayName}</span>
              <img src={profileIcon} alt="" className="site-banner__profile-icon" />
            </span>
          </div>
        </div>
      </div>

      <header className="site-header grid-item">
        <div className="site-header__inner">
          {/* Branding only: the logo is intentionally not a link, so the header offers no
              navigation out of the profile flow (MSP-7512 / MSP-7508). */}
          <span className="site-header__logo-link">
            <img src={scGovLogo} alt="SC.GOV" className="site-header__logo" />
          </span>
          <button
            type="button"
            className="site-header__menu-toggle"
            aria-expanded={headerMenuOpen}
            aria-label={headerMenuOpen ? 'Close menu' : 'Open menu'}
            onClick={toggleHeaderMenu}
          >
            <span className="site-header__menu-label">{headerMenuOpen ? 'CLOSE' : 'MENU'}</span>
            <span className={`site-header__menu-icon ${headerMenuOpen ? 'site-header__menu-icon--open' : ''}`}>
              <span /><span /><span />
            </span>
          </button>
        </div>
        {headerMenuOpen && (
          <div className="site-header__mobile-menu">
            {/* The site banner is hidden at <=1024px, so this menu is the only place the
                return link and Help Center are reachable on tablet/mobile. */}
            <div className="site-header__mobile-links">
              <a href={returnHref} className="site-header__mobile-link" onClick={handleReturnClick}>{returnLabel}</a>
              <a href={`${CONFIG.SC_GOV_URL}/help-center`} className="site-header__mobile-link">Help Center</a>
            </div>
          </div>
        )}
      </header>

      <div className="site-header__rule grid-item" />

      <div className="mobile-nav grid-item">
        <MobileNav routes={mobileNavRoutes} activePath={activePath} />
      </div>

      <div className="page grid-item m-6">
        <div className="page-heading">
          <a href={returnHref} className="page-heading__back" onClick={handleReturnClick}>
            <ForgeIcon name="arrow_back" />
            <span>{returnLabel}</span>
          </a>
          <h1 className="page-heading__title">Profile</h1>
          <p className="page-heading__subtitle">Manage your settings and personal information.</p>
        </div>
        <hr className="page-heading__divider" />
        <div className="page-body">
          <SideNav navRoutes={navRoutes} activeNavKey={activeNavKey} onNavigate={onNavigate} />

          <main id="main-content" className="grid-item">
            <div className="page-header">
              <h2 className="page-header__title">
                {currentRoute?.title}
              </h2>
              {(action || secondaryAction) ? (
                <div className="page-header__actions">
                  {secondaryAction ? (
                    <button
                      type="button"
                      className="page-header__action page-header__action--secondary"
                      onClick={handleSecondaryActionClick}>
                      {secondaryAction.label}
                    </button>
                  ) : null}
                  {action ? (
                    <button
                      type="button"
                      className="page-header__action"
                      disabled={actionState.disabled}
                      onClick={handleActionClick}>
                      {actionState.label || action.label}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
            {children}
          </main>
        </div>
      </div>


      <footer className="site-footer grid-item">
        {/* Branding only: the logo is intentionally not a link, so the footer offers no
            navigation out of the profile flow (MSP-7512 / MSP-7508). */}
        <div className="site-footer__top">
          <img src={scGovLogoWhite} alt="SC.GOV" className="site-footer__logo" />
        </div>
        <div className="site-footer__bottom">
          <nav className="site-footer__policies" aria-label="Legal">
            <a href={`${CONFIG.SC_GOV_URL}/accessibility-policy`}>Accessibility Policy</a>
            <a href={`${CONFIG.SC_GOV_URL}/linking-policy`}>Linking Policy</a>
            <a href={`${CONFIG.SC_GOV_URL}/privacy-and-security-policy`}>Privacy &amp; Security Policy</a>
            <a href={`${CONFIG.SC_GOV_URL}/terms-and-conditions`}>Terms &amp; Conditions</a>
          </nav>
          <p className="site-footer__copyright">Copyright &copy; <em>{new Date().getFullYear()}</em> State of South Carolina</p>
        </div>
      </footer>

      <IdleDialog />
    </div>
  );
}
